import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'react-hot-toast';
import { Send, CheckCircle } from 'lucide-react';

// Detectar API dinámicamente usando el dominio actual (igual que App.js)
const API = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:8001'
  : window.location.origin;

const FormularioConsultaVisitante = () => {
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [formularioActivo, setFormularioActivo] = useState(false);
  const [verificandoEstado, setVerificandoEstado] = useState(true);
  const [anuncio, setAnuncio] = useState(null);
  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    pais: '',
    ciudad: '',
    mensaje: ''
  });

  // Verificar si el formulario está activo
  useEffect(() => {
    const verificarEstado = async () => {
      try {
        // Agregar timestamp para evitar caché
        const response = await axios.get(`${API}/api/public/formulario-registraciones-activo?_t=${Date.now()}`);
        setFormularioActivo(response.data.activo);
      } catch (error) {
        console.error('Error verificando estado del formulario:', error);
        // FORZAR ACTIVO EN CASO DE ERROR
        setFormularioActivo(true);
      } finally {
        setVerificandoEstado(false);
      }
    };
    verificarEstado();
  }, []);

  // Cargar configuración del anuncio lateral
  useEffect(() => {
    const cargarAnuncio = async () => {
      try {
        console.log('🔍 Cargando anuncio desde:', `${API}/api/public/anuncio-lateral-consultas`);
        const response = await axios.get(`${API}/api/public/anuncio-lateral-consultas`);
        console.log('📦 Respuesta anuncio:', response.data);
        if (response.data.activo) {
          console.log('✅ Anuncio activo, mostrando');
          setAnuncio(response.data);
        } else {
          console.log('❌ Anuncio inactivo');
        }
      } catch (error) {
        console.error('❌ Error cargando anuncio:', error);
      }
    };
    cargarAnuncio();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre_completo || !formData.email || !formData.telefono || 
        !formData.pais || !formData.ciudad || !formData.mensaje) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setEnviando(true);

    try {
      await axios.post(`${API}/api/public/consulta-visitante`, formData);
      
      toast.success('¡Consulta enviada correctamente!');
      setEnviado(true);
      
      // Limpiar formulario
      setFormData({
        nombre_completo: '',
        email: '',
        telefono: '',
        pais: '',
        ciudad: '',
        mensaje: ''
      });

      // Volver a mostrar formulario después de 5 segundos
      setTimeout(() => setEnviado(false), 5000);

    } catch (error) {
      console.error('Error:', error);
      const mensaje = error.response?.data?.detail || 'Error al enviar consulta. Por favor intenta nuevamente.';
      toast.error(mensaje);
    } finally {
      setEnviando(false);
    }
  };

  // Mientras verifica estado
  if (verificandoEstado) {
    return null; // No mostrar nada mientras carga
  }

  // Si el formulario está desactivado, no mostrar nada
  if (!formularioActivo) {
    return null;
  }

  if (enviado) {
    return (
      <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 shadow-xl">
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
          <h3 className="text-3xl font-bold text-gray-800 mb-4">
            ¡Gracias por tu Interés!
          </h3>
          <p className="text-lg text-gray-700 mb-2">
            Tu consulta ha sido enviada correctamente.
          </p>
          <p className="text-gray-600">
            Te contactaremos pronto con las instrucciones de registro.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Mapeo de tamaños de fuente
  const tamanosTitulo = {
    'pequeño': 'text-xl',
    'mediano': 'text-2xl',
    'grande': 'text-3xl',
    'extra-grande': 'text-4xl md:text-5xl'
  };

  const tamanosDescripcion = {
    'pequeño': 'text-sm',
    'mediano': 'text-base',
    'grande': 'text-lg'
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  console.log('🎨 Renderizando FormularioConsultaVisitante');
  console.log('📊 Estado anuncio:', anuncio);
  console.log('🔢 Anuncio activo?', anuncio ? 'SÍ' : 'NO');

  return (
    <div className={`grid ${anuncio ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6 ${!anuncio ? 'max-w-3xl mx-auto' : ''}`}>
      {/* COLUMNA 1: FORMULARIO */}
      <Card className="bg-white shadow-2xl border-2 border-purple-200">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <h3 className="text-3xl font-bold text-center">
            ¿Quieres Estudiar con Nosotros?
          </h3>
          <p className="text-center text-purple-100 mt-2">
            Solicita información y te enviaremos las instrucciones de registro
          </p>
        </CardHeader>

        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nombre Completo */}
          <div>
            <Label htmlFor="nombre_completo" className="text-lg font-semibold text-gray-700">
              Nombre Completo <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nombre_completo"
              name="nombre_completo"
              type="text"
              required
              placeholder="Ej: Juan Pérez"
              value={formData.nombre_completo}
              onChange={handleChange}
              className="mt-2 h-12 text-lg"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-lg font-semibold text-gray-700">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              placeholder="tu@email.com"
              value={formData.email}
              onChange={handleChange}
              className="mt-2 h-12 text-lg"
            />
          </div>

          {/* Teléfono */}
          <div>
            <Label htmlFor="telefono" className="text-lg font-semibold text-gray-700">
              Teléfono / Celular <span className="text-red-500">*</span>
            </Label>
            <Input
              id="telefono"
              name="telefono"
              type="tel"
              required
              placeholder="+598 99 123 456"
              value={formData.telefono}
              onChange={handleChange}
              className="mt-2 h-12 text-lg"
            />
          </div>

          {/* País y Ciudad */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pais" className="text-lg font-semibold text-gray-700">
                País <span className="text-red-500">*</span>
              </Label>
              <Input
                id="pais"
                name="pais"
                type="text"
                required
                placeholder="Ej: Uruguay"
                value={formData.pais}
                onChange={handleChange}
                className="mt-2 h-12 text-lg"
              />
            </div>

            <div>
              <Label htmlFor="ciudad" className="text-lg font-semibold text-gray-700">
                Ciudad <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ciudad"
                name="ciudad"
                type="text"
                required
                placeholder="Ej: Montevideo"
                value={formData.ciudad}
                onChange={handleChange}
                className="mt-2 h-12 text-lg"
              />
            </div>
          </div>

          {/* Mensaje */}
          <div>
            <Label htmlFor="mensaje" className="text-lg font-semibold text-gray-700">
              Mensaje / Consulta <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="mensaje"
              name="mensaje"
              required
              rows="4"
              placeholder="Cuéntanos qué información necesitas o qué te gustaría estudiar..."
              value={formData.mensaje}
              onChange={handleChange}
              className="mt-2 w-full text-lg border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-sm text-gray-700">
              <strong>📧 ¿Qué recibirás?</strong> Te enviaremos un email con las instrucciones 
              detalladas para registrarte en nuestro sistema y comenzar tu formación.
            </p>
          </div>

          {/* Botón Submit */}
          <Button
            type="submit"
            disabled={enviando}
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
          >
            {enviando ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Enviando...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Send className="w-6 h-6 mr-2" />
                Enviar Consulta
              </span>
            )}
          </Button>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-4">
            Te responderemos en un plazo de 24-48 horas
          </p>
        </form>
      </CardContent>
    </Card>

      {/* COLUMNA 2: ANUNCIO LATERAL (si está activo) */}
      {anuncio && (
        <Card 
          className="shadow-2xl border-2 h-fit lg:sticky lg:top-4"
          style={{ backgroundColor: anuncio.fondo_color }}
        >
          <CardContent className="p-8">
            {/* Video de YouTube */}
            {anuncio.tipo_contenido === 'video' && anuncio.url_youtube && (
              <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="280"
                  src={getYoutubeEmbedUrl(anuncio.url_youtube)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full"
                ></iframe>
              </div>
            )}

            {/* Imagen */}
            {anuncio.tipo_contenido === 'imagen' && anuncio.url_imagen && (
              <div className="mb-6">
                <img
                  src={anuncio.url_imagen}
                  alt="Anuncio"
                  className="w-full h-64 object-cover rounded-xl shadow-lg"
                />
              </div>
            )}

            {/* Título */}
            {anuncio.titulo && (
              <h2
                className={`${tamanosTitulo[anuncio.titulo_tamano]} font-bold mb-4 leading-tight`}
                style={{ 
                  color: anuncio.titulo_color,
                  fontFamily: anuncio.titulo_fuente === 'Playfair' ? 'serif' : 'sans-serif'
                }}
              >
                {anuncio.titulo}
              </h2>
            )}

            {/* Descripción */}
            {anuncio.descripcion && (
              <p
                className={`${tamanosDescripcion[anuncio.descripcion_tamano]} mb-6 whitespace-pre-wrap leading-relaxed`}
                style={{ color: anuncio.descripcion_color }}
              >
                {anuncio.descripcion}
              </p>
            )}

            {/* Botón de Acción */}
            {anuncio.boton_activo && anuncio.boton_texto && anuncio.boton_link && (
              <a
                href={anuncio.boton_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 rounded-lg text-white font-bold text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                style={{ backgroundColor: anuncio.boton_color }}
              >
                {anuncio.boton_texto}
              </a>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FormularioConsultaVisitante;
